import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, Activity, Database, CreditCard, Shield, Wifi, Cpu, HardDrive, MemoryStick, Users, Briefcase, Receipt, FileText, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const config = {
    healthy: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', dot: 'bg-emerald-400', label: 'Healthy' },
    degraded: { bg: 'bg-amber-500/15', text: 'text-amber-400', dot: 'bg-amber-400', label: 'Degraded' },
    unhealthy: { bg: 'bg-red-500/15', text: 'text-red-400', dot: 'bg-red-400', label: 'Unhealthy' },
    not_configured: { bg: 'bg-gray-500/15', text: 'text-gray-400', dot: 'bg-gray-500', label: 'Not Configured' },
    partial: { bg: 'bg-amber-500/15', text: 'text-amber-400', dot: 'bg-amber-400', label: 'Partial' },
  };
  const c = config[status] || config.unhealthy;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot} animate-pulse`} />
      {c.label}
    </span>
  );
};

const ServiceCard = ({ title, icon: Icon, data, accent }) => (
  <div className={`bg-gray-800/60 border border-gray-700/50 rounded-xl p-5 hover:border-${accent}-500/30 transition-all`} data-testid={`health-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2.5">
        <div className={`p-2 rounded-lg bg-${accent}-500/10`}>
          <Icon className={`w-5 h-5 text-${accent}-400`} />
        </div>
        <h3 className="text-white font-semibold text-sm">{title}</h3>
      </div>
      <StatusBadge status={data?.status} />
    </div>
    <div className="space-y-1.5">
      {data?.latency_ms !== undefined && data?.latency_ms > 0 && (
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Latency</span>
          <span className={`font-mono ${data.latency_ms < 100 ? 'text-emerald-400' : data.latency_ms < 500 ? 'text-amber-400' : 'text-red-400'}`}>{data.latency_ms}ms</span>
        </div>
      )}
      {data?.collections !== undefined && (
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Collections</span>
          <span className="text-gray-300 font-mono">{data.collections}</span>
        </div>
      )}
      {data?.documents !== undefined && (
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Documents</span>
          <span className="text-gray-300 font-mono">{data.documents.toLocaleString()}</span>
        </div>
      )}
      {data?.data_size_mb !== undefined && (
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Data Size</span>
          <span className="text-gray-300 font-mono">{data.data_size_mb} MB</span>
        </div>
      )}
      {data?.provider && (
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Provider</span>
          <span className="text-gray-300">{data.provider}</span>
        </div>
      )}
      {data?.active_connections !== undefined && (
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Active Connections</span>
          <span className="text-gray-300 font-mono">{data.active_connections}</span>
        </div>
      )}
      {data?.chat_sockets !== undefined && (
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Open Sockets</span>
          <span className="text-gray-300 font-mono">{data.chat_sockets}</span>
        </div>
      )}
      {data?.job_queue && (
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Job Queue</span>
          <span className="text-gray-300 font-mono">{data.job_queue.pending} pending / {data.job_queue.workers}w</span>
        </div>
      )}
      {data?.total_subscribers !== undefined && (
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Pub/Sub Subscribers</span>
          <span className="text-gray-300 font-mono">{data.total_subscribers}</span>
        </div>
      )}
      {data?.masked_url && (
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Endpoint</span>
          <span className="text-gray-300 font-mono truncate max-w-[140px]" title={data.masked_url}>{data.masked_url.replace(/^rediss?:\/\/[^@]*@/, '')}</span>
        </div>
      )}
      {data?.message && (
        <p className="text-gray-500 text-xs mt-1">{data.message}</p>
      )}
      {data?.error && (
        <p className="text-red-400 text-xs mt-1 truncate" title={data.error}>{data.error}</p>
      )}
    </div>
  </div>
);

const ResourceBar = ({ label, value, icon: Icon, color, suffix = '%' }) => (
  <div>
    <div className="flex justify-between items-center mb-1.5">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 text-${color}-400`} />
        <span className="text-gray-400 text-xs">{label}</span>
      </div>
      <span className={`text-xs font-mono ${value > 85 ? 'text-red-400' : value > 65 ? 'text-amber-400' : 'text-emerald-400'}`}>
        {value}{suffix}
      </span>
    </div>
    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-700 ${value > 85 ? 'bg-red-500' : value > 65 ? 'bg-amber-500' : 'bg-emerald-500'}`}
        style={{ width: `${Math.min(value, 100)}%` }}
      />
    </div>
  </div>
);

const SystemHealthTab = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  const fetchHealth = useCallback(async (isManual = false) => {
    try {
      if (isManual) setRefreshing(true);
      else setLoading(true);
      const { data } = await axios.get(`${API}/owner/system-health`);
      setHealth(data);
      setLastChecked(new Date());
    } catch (err) {
      console.error('Health check failed:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchHealth(true), 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchHealth]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-3 text-gray-400">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Running health checks...</span>
        </div>
      </div>
    );
  }

  const sys = health?.system_resources || {};
  const data = health?.data_summary || {};
  const services = health?.services || {};

  return (
    <div data-testid="system-health-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Activity className="w-6 h-6 text-blue-400" />
            System Health
          </h2>
          <p className="text-gray-500 text-sm mt-1">Real-time platform service monitoring</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${autoRefresh ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-500/30' : 'bg-gray-700 text-gray-400 border border-gray-600'}`}
            data-testid="auto-refresh-toggle"
          >
            {autoRefresh ? 'Auto-Refresh: ON' : 'Auto-Refresh: OFF'}
          </button>
          <button
            onClick={() => fetchHealth(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-1.5 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-lg text-xs font-medium hover:bg-blue-600/30 disabled:opacity-40 transition"
            data-testid="refresh-health-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Overall Status Banner */}
      <div className={`rounded-xl p-4 mb-6 border ${
        health?.overall_status === 'healthy' ? 'bg-emerald-900/10 border-emerald-700/30' :
        health?.overall_status === 'degraded' ? 'bg-red-900/10 border-red-700/30' :
        'bg-amber-900/10 border-amber-700/30'
      }`} data-testid="overall-status">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full animate-pulse ${
              health?.overall_status === 'healthy' ? 'bg-emerald-400' :
              health?.overall_status === 'degraded' ? 'bg-red-400' : 'bg-amber-400'
            }`} />
            <span className="text-white font-semibold">
              {health?.overall_status === 'healthy' ? 'All Systems Operational' :
               health?.overall_status === 'degraded' ? 'System Degraded' : 'Partial Service'}
            </span>
            <StatusBadge status={health?.overall_status} />
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span>Uptime: <span className="text-white font-mono">{health?.uptime}</span></span>
            {lastChecked && <span>Checked: {lastChecked.toLocaleTimeString()}</span>}
          </div>
        </div>
      </div>

      {/* Service Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <ServiceCard title="Database" icon={Database} data={services.database} accent="blue" />
        <ServiceCard title="Payment Gateway" icon={CreditCard} data={services.payment_gateway} accent="purple" />
        <ServiceCard title="KYC Verification" icon={Shield} data={services.kyc_verification} accent="amber" />
        <ServiceCard title="WebSocket" icon={Wifi} data={services.websocket} accent="emerald" />
        <ServiceCard title="Redis Cache" icon={Zap} data={services.cache} accent="orange" />
      </div>

      {/* System Resources */}
      <div className="bg-gray-800/60 border border-gray-700/50 rounded-xl p-6 mb-6" data-testid="system-resources">
        <h3 className="text-white font-semibold mb-4">System Resources</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ResourceBar label="CPU Usage" value={sys.cpu_percent || 0} icon={Cpu} color="blue" />
          <ResourceBar label="Memory Usage" value={sys.memory_percent || 0} icon={MemoryStick} color="purple" />
          <ResourceBar label="Disk Usage" value={sys.disk_percent || 0} icon={HardDrive} color="amber" />
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-700/50">
          <div className="text-center">
            <p className="text-xs text-gray-500">Memory</p>
            <p className="text-sm text-gray-300 font-mono">{((sys.memory_used_mb || 0) / 1024).toFixed(1)} / {((sys.memory_total_mb || 0) / 1024).toFixed(1)} GB</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Disk</p>
            <p className="text-sm text-gray-300 font-mono">{sys.disk_used_gb || 0} / {sys.disk_total_gb || 0} GB</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">CPU</p>
            <p className="text-sm text-gray-300 font-mono">{sys.cpu_percent || 0}%</p>
          </div>
        </div>
      </div>

      {/* Data Summary */}
      <div className="bg-gray-800/60 border border-gray-700/50 rounded-xl p-6" data-testid="data-summary">
        <h3 className="text-white font-semibold mb-4">Data Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Users', value: data.users, icon: Users, color: 'blue' },
            { label: 'Bookings', value: data.bookings, icon: Briefcase, color: 'purple' },
            { label: 'Transactions', value: data.transactions, icon: Receipt, color: 'emerald' },
            { label: 'Jobs', value: data.jobs, icon: FileText, color: 'amber' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="text-center p-4 bg-gray-700/30 rounded-lg">
              <Icon className={`w-5 h-5 text-${color}-400 mx-auto mb-2`} />
              <p className="text-2xl font-bold text-white">{(value || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SystemHealthTab;
